import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { AlertComponent, DialogComponent } from '@components';
import { Status } from '@enums';
import { ServiceProvider } from '@models';
import { UtilService } from '@services';
import {
  fetchCurrentServiceProvider,
  selectCurrentServiceProvider,
  selectServiceProvidersFetchedAt,
  updateServiceProvider,
  updateServiceProviderFailed,
  updateServiceProviderImage,
  updateServiceProviderImageFailed,
  updateServiceProviderImageSuccess,
  updateServiceProviderSuccess,
} from '@state';
import { ServiceProviderAdminsComponent } from './service-provider-admins/service-provider-admins.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatCardModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    AlertComponent,
    ServiceProviderAdminsComponent,
  ],
  templateUrl: './service-provider.page.html',
  styleUrl: './service-provider.page.scss',
})
export class ServiceProviderPage implements OnInit, OnDestroy {
  readonly dialog = inject(MatDialog);

  public loading: boolean = false;
  public error: string | null = null;
  public serviceProvider: ServiceProvider = new ServiceProvider();
  private sub: Subscription | null = null;

  public $fetchedAt = this.store.select(selectServiceProvidersFetchedAt);
  private $serviceProvider = this.store.select(selectCurrentServiceProvider);

  public form = new FormGroup({
    name: new FormControl('', [Validators.required]),
  });

  constructor(
    private store: Store,
    public utils: UtilService
  ) {}

  async ngOnInit(): Promise<void> {
    this.store.dispatch(fetchCurrentServiceProvider());

    this.sub = this.$serviceProvider.subscribe((serviceProvider) => {
      if (!serviceProvider) return;
      this.serviceProvider = serviceProvider;
      this.populateForm(serviceProvider);
    });
  }

  private populateForm(serviceProvider: ServiceProvider): void {
    this.form.patchValue({ name: serviceProvider.name });
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
  }

  public getFormControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  public async onFormSubmit(): Promise<void> {
    if (!this.form.valid) {
      this.error = 'Please fill in all required fields';
      return;
    }

    this.loading = true;

    try {
      const serviceProvider: ServiceProvider = {
        ...this.serviceProvider,
        name: this.getFormControl('name').value,
      };

      await this.updateServiceProvider(serviceProvider);

      this.showSuccessModal();
    } catch (err: any) {
      this.error = this.utils.getErrorMessage(err);
    }

    this.loading = false;
  }

  private async updateServiceProvider(serviceProvider: ServiceProvider): Promise<void> {
    await this.utils.actionsToPromise(
      updateServiceProvider({ serviceProvider }),
      updateServiceProviderSuccess,
      updateServiceProviderFailed
    );
  }

  private showSuccessModal(): void {
    this.dialog.open(DialogComponent, {
      data: {
        title: 'Success',
        message: 'Service Provider updated successfully',
      },
    });
  }

  public onToggleServiceProviderStatusClicked(): void {
    const toggleText =
      this.serviceProvider.status == Status.APPROVED ? 'Disable' : 'Enable';

    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        title: `${toggleText} Service Provider`,
        message: `Are you sure you want to ${toggleText} this service provider?`,
        cancel: true,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.toggleServiceProviderStatus({ ...this.serviceProvider });
    });
  }

  private async toggleServiceProviderStatus(serviceProvider: ServiceProvider): Promise<void> {
    this.loading = true;

    try {
      if (serviceProvider.status == Status.APPROVED) {
        serviceProvider.status = Status.DECLINED;
      } else {
        serviceProvider.status = Status.APPROVED;
      }

      await this.updateServiceProvider(serviceProvider);
      this.showSuccessModal();
    } catch (err: any) {
      this.error = this.utils.getErrorMessage(err);
    }

    this.loading = false;
  }

  public async onFileChange(event: any): Promise<void> {
    const file = event.target.files[0];
    if (!file) return;

    this.loading = true;
    try {
      await this.utils.actionsToPromise(
        updateServiceProviderImage({ serviceProvider: this.serviceProvider, file }),
        updateServiceProviderImageSuccess,
        updateServiceProviderImageFailed
      );
      this.showSuccessModal();
    } catch (err: any) {
      this.error = this.utils.getErrorMessage(err);
    }
    this.loading = false;
  }
}


